/**
 * Step 2: Entry & Exit Rules
 *
 * - Entry rule dropdown (single select)
 * - Exit rule dropdown (single select)
 * - Dynamic parameters with info icon popovers
 */

import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import ParamInput from '../shared/ParamInput';
import InfoPopover from '../shared/InfoPopover';

const EntryExitStep = ({ entryComponents, exitComponents, formData, updateFormData, errors }) => {
  // Select entry rule
  const handleSelectEntry = (componentId) => {
    const component = entryComponents.find(c => c.componentId === componentId);
    if (!component) {
      updateFormData({ entryRule: null });
      return;
    }

    const defaultValues = {};
    component.parameters.forEach(param => {
      if (param.defaultValue !== undefined && param.defaultValue !== null) {
        defaultValues[param.name] = param.defaultValue;
      }
    });

    updateFormData({
      entryRule: {
        componentId,
        component,
        parameters: component.parameters,
        values: defaultValues
      }
    });
  };

  // Select exit rule
  const handleSelectExit = (componentId) => {
    const component = exitComponents.find(c => c.componentId === componentId);
    if (!component) {
      updateFormData({ exitRule: null });
      return;
    }

    const defaultValues = {};
    component.parameters.forEach(param => {
      if (param.defaultValue !== undefined && param.defaultValue !== null) {
        defaultValues[param.name] = param.defaultValue;
      }
    });

    updateFormData({
      exitRule: {
        componentId,
        component,
        parameters: component.parameters,
        values: defaultValues
      }
    });
  };

  // Update parameter value
  const handleParamChange = (ruleType, paramName, value) => {
    const rule = ruleType === 'entry' ? formData.entryRule : formData.exitRule;
    if (!rule) return;

    const updated = {
      ...rule,
      values: { ...rule.values, [paramName]: value }
    };

    updateFormData({
      [ruleType === 'entry' ? 'entryRule' : 'exitRule']: updated
    });
  };

  // Render rule card
  const renderRuleCard = (title, ruleType, components, selected, color) => {
    const selectHandler = ruleType === 'entry' ? handleSelectEntry : handleSelectExit;
    const error = errors[`${ruleType}Rule`];

    return (
      <Card
        sx={{
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: `1px solid ${error ? '#ef4444' : 'rgba(255,255,255,0.1)'}`,
          borderRadius: '8px',
          mb: 3
        }}
      >
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color, mb: 2 }}>
            {title}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
              {error}
            </Alert>
          )}

          {/* Rule Selector */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Select {title}</InputLabel>
            <Select
              value={selected?.componentId || ''}
              onChange={(e) => selectHandler(e.target.value)}
              label={`Select ${title}`}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.05)',
                color: '#fff',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: color }
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {components.map(comp => (
                <MenuItem key={comp.componentId} value={comp.componentId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography>{comp.name}</Typography>
                    {comp.tooltip && (
                      <Box sx={{ ml: 'auto' }}>
                        <InfoPopover
                          title={comp.name}
                          description={comp.tooltip}
                          tags={comp.tags}
                          accentColor={color}
                          iconSx={{ color: 'rgba(0,0,0,0.4)', '&:hover': { color: color } }}
                        />
                      </Box>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Parameters */}
          {selected && selected.parameters.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
                Parameters
              </Typography>
              <Grid container spacing={2}>
                {selected.parameters.map(param => (
                  <Grid item xs={12} sm={6} key={param.name}>
                    <ParamInput
                      param={param}
                      value={selected.values[param.name] ?? param.defaultValue ?? ''}
                      onChange={(value) => handleParamChange(ruleType, param.name, value)}
                      accentColor={color}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1, color: '#fff' }}>
        Entry & Exit Rules
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255,255,255,0.6)' }}>
        Define when to enter and exit trades based on indicator signals.
      </Typography>

      {/* Entry Rule */}
      {renderRuleCard('Entry Rule', 'entry', entryComponents, formData.entryRule, '#27B56D')}

      {/* Exit Rule */}
      {renderRuleCard('Exit Rule', 'exit', exitComponents, formData.exitRule, '#ef4444')}
    </Box>
  );
};

export default EntryExitStep;
